/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { QuestionnaireActivityTaskReq } from './questionnaire-activity-task-req'
import { QuestionnaireActivityTaskRes } from './questionnaire-activity-task-res'
import { QuestionnaireSubmitRes } from './questionnaire-submit-res'
import { TaskReflection } from './task-reflection'
import { QuestionnaireSubmitReq } from './questionnaire-submit-req'
import { QueryQuestionnaireTaskReflectionsQuery } from './query-questionnaire-task-reflections-query'
import { QueryExportedQuestionnaireReflectionsQuery } from './query-exported-questionnaire-reflections-query'

const buildConfig = createConfigBuilder('growp')

export const questionnaireApi = {
  createQuestionnaireActivityTaskConfig(
    questionnaireActivityTaskReq: QuestionnaireActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/questionnaire/activity-tasks',
      {},
      null,
      questionnaireActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          questionnaires: (v: any) => v || [],
          isUpdateFields: (v: any) => v || false,
        },
      }
    )
  },

  /**
   * @summary 【后台】问卷-创建活动任务
   * @param questionnaireActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<object>
   */
  createQuestionnaireActivityTask(
    questionnaireActivityTaskReq: QuestionnaireActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<object> {
    return axios(
      questionnaireApi.createQuestionnaireActivityTaskConfig(
        questionnaireActivityTaskReq,
        options
      )
    ) as any
  },

  useCreateQuestionnaireActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: QuestionnaireActivityTaskReq) =>
        questionnaireApi.createQuestionnaireActivityTask(payload),
      mutationOptions
    )
  },

  updateQuestionnaireActivityTaskConfig(
    id: number,
    questionnaireActivityTaskReq: QuestionnaireActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/questionnaire/activity-tasks/{id}',
      {
        id,
      },
      null,
      questionnaireActivityTaskReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          questionnaires: (v: any) => v || [],
          isUpdateFields: (v: any) => v || false,
        },
      }
    )
  },

  /**
   * @summary 【后台】问卷-编辑活动任务
   * @param id
   * @param questionnaireActivityTaskReq
   * @param [options]
   * @return AxiosResponsePromise<object>
   */
  updateQuestionnaireActivityTask(
    id: number,
    questionnaireActivityTaskReq: QuestionnaireActivityTaskReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<object> {
    return axios(
      questionnaireApi.updateQuestionnaireActivityTaskConfig(
        id,
        questionnaireActivityTaskReq,
        options
      )
    ) as any
  },

  useUpdateQuestionnaireActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        id,
        payload,
      }: {
        id: number
        payload: QuestionnaireActivityTaskReq
      }) => questionnaireApi.updateQuestionnaireActivityTask(id, payload),
      mutationOptions
    )
  },

  loadQuestionnaireActivityTaskConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/questionnaire/activity-tasks/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】问卷-详情
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<QuestionnaireActivityTaskRes>
   */
  loadQuestionnaireActivityTask(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<QuestionnaireActivityTaskRes> {
    return axios(
      questionnaireApi.loadQuestionnaireActivityTaskConfig(id, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new QuestionnaireActivityTaskRes(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadQuestionnaireActivityTask(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<QuestionnaireActivityTaskRes> {
    return useQueryApi<QuestionnaireActivityTaskRes>(
      questionnaireApi.loadQuestionnaireActivityTask,
      'Questionnaire.loadQuestionnaireActivityTask',
      queryOptions,
      id
    )
  },

  loadForeEndQuestionnaireActivityTaskConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/questionnaire/activity-tasks/{id}/fore-end',
      {
        id,
      }
    )
  },

  /**
   * @summary 【前台】问卷-详情
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<QuestionnaireActivityTaskRes>
   */
  loadForeEndQuestionnaireActivityTask(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<QuestionnaireActivityTaskRes> {
    return axios(
      questionnaireApi.loadForeEndQuestionnaireActivityTaskConfig(id, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new QuestionnaireActivityTaskRes(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadForeEndQuestionnaireActivityTask(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<QuestionnaireActivityTaskRes> {
    return useQueryApi<QuestionnaireActivityTaskRes>(
      questionnaireApi.loadForeEndQuestionnaireActivityTask,
      'Questionnaire.loadForeEndQuestionnaireActivityTask',
      queryOptions,
      id
    )
  },

  loadSubmitActivityTaskConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/questionnaire/activity-tasks/{id}/info',
      {
        id,
      }
    )
  },

  /**
   * @summary 【前台】问卷-学生提交问卷详情
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<QuestionnaireSubmitRes[]>
   */
  loadSubmitActivityTask(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<QuestionnaireSubmitRes[]> {
    return axios(
      questionnaireApi.loadSubmitActivityTaskConfig(id, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data
        ? _data.map((d: any) => new QuestionnaireSubmitRes(d))
        : _data
      return { data, ...rest }
    }) as any
  },

  useLoadSubmitActivityTask(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<QuestionnaireSubmitRes[]> {
    return useQueryApi<QuestionnaireSubmitRes[]>(
      questionnaireApi.loadSubmitActivityTask,
      'Questionnaire.loadSubmitActivityTask',
      queryOptions,
      id
    )
  },

  checkQuestionnaireTaskIsSubmitConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/questionnaire/activity-tasks/{id}/is-submit',
      {
        id,
      }
    )
  },

  /**
   * @summary 【后台】问卷-查询是否已有学生提交
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  checkQuestionnaireTaskIsSubmit(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      questionnaireApi.checkQuestionnaireTaskIsSubmitConfig(id, options)
    ) as any
  },

  useCheckQuestionnaireTaskIsSubmit(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<number> {
    return useQueryApi<number>(
      questionnaireApi.checkQuestionnaireTaskIsSubmit,
      'Questionnaire.checkQuestionnaireTaskIsSubmit',
      queryOptions,
      id
    )
  },

  queryQuestionnaireTaskReflectionsConfig(
    id: number,
    queryQuestionnaireTaskReflectionsQuery?: QueryQuestionnaireTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/questionnaire/activity-tasks/{id}/reflections',
      {
        id,
      },
      queryQuestionnaireTaskReflectionsQuery
    )
  },

  /**
   * @summary 【后台】问卷任务-问卷数据-任务人员列表(完成情况)
   * @param id
   * @param [queryQuestionnaireTaskReflectionsQuery]
   * @param [queryQuestionnaireTaskReflectionsQuery.expand]
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.nameIn]
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.nameLike]
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.accountLike]
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.accountIn]
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.genderIn]
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.statusIn]
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.phoneIn]
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.phoneLike]
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.emailIn]
   * @param [queryQuestionnaireTaskReflectionsQuery.reflection.emailLike]
   * @param [queryQuestionnaireTaskReflectionsQuery.sort]
   * @param [queryQuestionnaireTaskReflectionsQuery.paginated] 是否分页，默认 1
   * @param [queryQuestionnaireTaskReflectionsQuery.page] 显示的数据页，默认 1
   * @param [queryQuestionnaireTaskReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection[]>
   */
  queryQuestionnaireTaskReflections<
    TQuery extends QueryQuestionnaireTaskReflectionsQuery
  >(
    id: number,
    queryQuestionnaireTaskReflectionsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<TaskReflection, TQuery['expand']>[]> {
    return axios(
      questionnaireApi.queryQuestionnaireTaskReflectionsConfig(
        id,
        queryQuestionnaireTaskReflectionsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new TaskReflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryQuestionnaireTaskReflections<
    TQuery extends QueryQuestionnaireTaskReflectionsQuery
  >(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<TaskReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<TaskReflection, TQuery['expand']>[]>(
      questionnaireApi.queryQuestionnaireTaskReflections,
      'Questionnaire.queryQuestionnaireTaskReflections',
      queryOptions,
      id,
      query
    )
  },

  queryExportedQuestionnaireReflectionsConfig(
    keys: string,
    idIn: string,
    id: number,
    queryExportedQuestionnaireReflectionsQuery?: QueryExportedQuestionnaireReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/questionnaire/activity-tasks/{id}/reflections/exported',
      {
        keys,
        idIn,
        id,
      },
      queryExportedQuestionnaireReflectionsQuery
    )
  },

  /**
   * @summary 问卷提交的数据-列表导出
   * @param keys
   * @param idIn
   * @param id
   * @param [queryExportedQuestionnaireReflectionsQuery]
   * @param [queryExportedQuestionnaireReflectionsQuery.onlyDefault]
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  queryExportedQuestionnaireReflections(
    keys: string,
    idIn: string,
    id: number,
    queryExportedQuestionnaireReflectionsQuery?: QueryExportedQuestionnaireReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      questionnaireApi.queryExportedQuestionnaireReflectionsConfig(
        keys,
        idIn,
        id,
        queryExportedQuestionnaireReflectionsQuery,
        options
      )
    ) as any
  },

  useQueryExportedQuestionnaireReflections(
    {
      keys,
      idIn,
      id,
      query,
    }: {
      keys: string
      idIn: string
      id: number
      query?: QueryExportedQuestionnaireReflectionsQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      questionnaireApi.queryExportedQuestionnaireReflections,
      'Questionnaire.queryExportedQuestionnaireReflections',
      queryOptions,
      keys,
      idIn,
      id,
      query
    )
  },

  submitQuestionnaireActivityTaskConfig(
    taskId: number,
    id: number,
    questionnaireSubmitReq: QuestionnaireSubmitReq[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/questionnaire/{id}/activity-tasks/{taskId}/submit',
      {
        taskId,
        id,
      },
      null,
      questionnaireSubmitReq,
      {
        format: 'json',
        isArray: true,
        defaults: {
          otherValue: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 【前台】问卷-学生提交问卷
   * @param taskId
   * @param id
   * @param questionnaireSubmitReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  submitQuestionnaireActivityTask(
    taskId: number,
    id: number,
    questionnaireSubmitReq: QuestionnaireSubmitReq[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      questionnaireApi.submitQuestionnaireActivityTaskConfig(
        taskId,
        id,
        questionnaireSubmitReq,
        options
      )
    ) as any
  },

  useSubmitQuestionnaireActivityTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        taskId,
        id,
        payload,
      }: {
        taskId: number
        id: number
        payload: QuestionnaireSubmitReq[]
      }) =>
        questionnaireApi.submitQuestionnaireActivityTask(taskId, id, payload),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(questionnaireApi).forEach((api) => {
  // @ts-ignore
  questionnaireApi[api]._name_ = `Questionnaire.${api}`
})
